import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Star, AlertCircle } from 'lucide-react'
import { usePicks } from '@/hooks/usePicks'
import { useViewSettings } from '@/hooks/useViewSettings'
import { SecondaryPickCard } from '@/components/SecondaryPickCard'
import { TopPickCard } from '@/components/TopPickCard'
import { PickDetailSheet } from '@/components/PickDetailSheet'
import { SkeletonCard } from '@/components/LoadingSkeleton'
import type { PickLeg } from '@/types/miniApp'

export function FavoritesPage() {
  const { favorites } = useViewSettings()
  const { data, isLoading, isError } = usePicks(true)
  const [selectedPick, setSelectedPick] = useState<PickLeg | null>(null)

  const allPicks = data ? [...(data.featured || []), ...(data.secondary || [])] : []

  // Cruzamos los IDs guardados en localStorage con los picks del portafolio actual
  const favoritePicks = allPicks.filter((pick) =>
    favorites.includes(String(pick.decision_record_id))
  )

  return (
    <div className="p-4 space-y-6">
      
      {/* Botón de retroceso y Título */}
      <div className="flex items-center gap-3">
        <Link
          to="/mas"
          className="p-2 rounded-xl bg-panel-elevated hover:bg-border text-text transition"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-lg font-bold text-text">Picks favoritos</h1>
          <p className="text-xs text-text-dim">Acceso rápido a tus apuestas seleccionadas</p>
        </div>
      </div>

      {/* Listado de Favoritos */}
      <div className="space-y-4">
        {isLoading && (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {isError && (
          <div className="card border-danger/30 bg-danger-soft/10 text-center py-6 space-y-2">
            <AlertCircle className="h-8 w-8 text-danger mx-auto" />
            <p className="text-xs text-text-muted">Error al verificar favoritos.</p>
          </div>
        )}

        {!isLoading && !isError && favoritePicks.length === 0 && (
          <div className="card border-border bg-panel-elevated/20 text-center py-12 text-text-dim space-y-3">
            <div className="relative mx-auto h-12 w-12 rounded-2xl bg-panel flex items-center justify-center border border-border">
              <Star className="h-6 w-6 text-text-dim/40" />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold uppercase tracking-wider text-text-muted">Sin favoritos</p>
              <p className="text-[11px] max-w-xs mx-auto leading-relaxed">
                Toca el icono de la estrella en cualquier tarjeta de pick (Top o Secundario) para guardarlo aquí.
              </p>
            </div>
          </div>
        )}

        {!isLoading && !isError && favoritePicks.length > 0 && (
          <div className="space-y-3">
            {favoritePicks.map((pick, i) => {
              const isTopPick = (pick.rank || 0) > 0
              return isTopPick ? (
                <TopPickCard
                  key={pick.decision_record_id}
                  pick={pick}
                  rank={pick.rank || i + 1}
                  onSelect={() => setSelectedPick(pick)}
                />
              ) : (
                <SecondaryPickCard
                  key={pick.decision_record_id}
                  pick={pick}
                  onSelect={() => setSelectedPick(pick)}
                />
              )
            })}
          </div>
        )}
      </div>

      {selectedPick && (
        <PickDetailSheet pick={selectedPick} onClose={() => setSelectedPick(null)} />
      )}
    </div>
  )
}
